import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, Collapse, Divider, Typography } from "@mui/material";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { Alert } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import type { MangoRepresentativeDetail } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientPhone, formatPersonalIdentificationNumber } from "Infrastructure/Utils/ClientUtils";
import { useState } from "react";
import { Flex } from "../Core/Flex";
import { HStack } from "../Core/Stack";
import { formatBirthDateWithAge, formatCityAndPostalCode } from "./ClientCardDetail.helpers";
import { InfoItem } from "./InfoItem";

const ICON_CIRCLE_SIZE = 36;

type Props = {
  representative: MangoRepresentativeDetail;
  showAdultWarning: boolean;
};

export const ClientCardLegalRepresentative = ({ representative, showAdultWarning }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formatLocale = useFormatLocale();
  const { t } = useResource();

  const fullName = getClientFullName({
    titlePrefix: representative.titlePrefix,
    name: representative.firstName,
    surname: representative.surname,
    titlePostfix: representative.titlePostfix,
  }).trim();
  const address = formatCityAndPostalCode(representative.address);

  return (
    <Box>
      <Divider />
      <HStack px={Spacing.XL} py={Spacing.L} spacing={Spacing.L} alignItems="center">
        <Flex
          flexShrink={0}
          width={ICON_CIRCLE_SIZE}
          height={ICON_CIRCLE_SIZE}
          borderRadius="50%"
          alignItems="center"
          justifyContent="center"
          color="text.secondary"
          sx={{ backgroundColor: (theme) => theme.colors.textTertiary }}
        >
          <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 20 }} />
        </Flex>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t(Resources.Clients.ClientCard.LegalRepresentative.Title)}
          </Typography>
          <Typography fontSize={15} fontWeight={500}>
            {fullName}
          </Typography>
        </Box>
        <Button
          size="small"
          color="secondary"
          onClick={() => setIsExpanded((prev) => !prev)}
          endAdornment={
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
            />
          }
        >
          {t(
            isExpanded
              ? Resources.Clients.ClientCard.LegalRepresentative.HideDetails
              : Resources.Clients.ClientCard.LegalRepresentative.ShowDetails,
          )}
        </Button>
      </HStack>

      <Collapse in={isExpanded}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(2, minmax(0, 1fr))"
          rowGap={Spacing.M}
          columnGap={Spacing.XXL}
          pr={Spacing.XL}
          pb={Spacing.XL}
          // lines the details up under the name rather than under the icon
          sx={(theme) => ({ pl: `calc(${theme.spacing(Spacing.XL + Spacing.L)} + ${ICON_CIRCLE_SIZE}px)` })}
        >
          <InfoItem icon={<CalendarTodayOutlinedIcon />} text={formatBirthDateWithAge(representative.birthDate, formatLocale, t)} />
          <InfoItem icon={<PhoneOutlinedIcon />} text={formatClientPhone(representative.phonePrefix, representative.phoneNationalNumber)} />
          <InfoItem icon={<BadgeOutlinedIcon />} text={formatPersonalIdentificationNumber(representative.personalIdentificationNumber)} />
          <InfoItem icon={<EmailOutlinedIcon />} text={representative.email} />
          {address && (
            <Box gridColumn="1 / -1">
              <InfoItem icon={<LocationOnOutlinedIcon />} text={address} />
            </Box>
          )}
        </Box>
      </Collapse>

      {showAdultWarning && (
        <Box px={Spacing.XL} pb={Spacing.XL}>
          <Alert
            variant="warning"
            title={t(Resources.Clients.ClientCard.Alerts.AdultWithLegalRepresentative.Title)}
            text={t(Resources.Clients.ClientCard.Alerts.AdultWithLegalRepresentative.Text)}
          />
        </Box>
      )}
    </Box>
  );
};
